import { DateTime } from 'luxon';
import { connect, ConnectedProps } from 'react-redux';
import { getFormValues, InjectedFormProps, reduxForm } from 'redux-form';

import DateInputField from 'dashboard/components/FormControls/DateInputField';
import RadioGroup from 'dashboard/components/FormControls/RadioGroup';
import EmptyRow from 'dashboard/layouts/FormLayout/EmptyRow';
import Form from 'dashboard/layouts/FormLayout/Form';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import FormControlField from 'lib/interfaces/FormControlField';
import { Iso8601DateString } from 'lib/types';
import { isValidDate, withMessage } from 'lib/utils/formValidation';
import { EMPTY_DATE } from 'lib/utils/formValidation/isRequiredDate';

const validate = {
  fromDate: [
    withMessage<Iso8601DateString, FormAuthEnforcementData>(
      'Enter a valid date in the future',
      (value, allValues) =>
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        allValues.startEnforcement === StartEnforcementValue.Immediately ||
        (value !== EMPTY_DATE &&
          isValidDate(value) &&
          DateTime.local().startOf('day') < DateTime.fromISO(value)),
    ),
  ],
};

const FormAuthEnforcement = ({
  showFromDate,
  handleSubmit,
  submitting,
}: InjectedFormProps<FormAuthEnforcementData> & PropsFromRedux) => {
  // initial value for startEnforcement should be immediately
  return (
    <Form onSubmit={handleSubmit}>
      <FormControlField
        label='Start enforcement'
        component={RadioGroup}
        name='startEnforcement'
        id='form-auth-enforcement__start-enforcement-radio'
        data-test='form-auth-enforcementt__start-enforcement-radio'
        disabled={submitting}
        isCompact
        radioLabelValues={[
          {
            label: 'Immediately',
            value: StartEnforcementValue.Immediately,
          },
          {
            label: 'From date',
            value: StartEnforcementValue.FromDate,
          },
        ]}
      />
      <FormControlField
        component={DateInputField}
        name='fromDate'
        id='form-auth-enforcement__from-date'
        disabled={submitting || !showFromDate}
        validate={validate.fromDate}
        yearMin={new Date().getFullYear()}
        yearMax={null}
      />
      <EmptyRow />
      <HStack equal={true}>
        <Button
          type='submit'
          width='button'
          id='form-auth-enforcement__btn--confirm'
          data-test='form-auth-enforcement__btn--confirm'
          isPending={submitting}
          disabled={submitting}
        >
          Enforce
        </Button>
      </HStack>
    </Form>
  );
};

export enum StartEnforcementValue {
  Immediately = 'immediately',
  FromDate = 'from-date',
}

export interface FormAuthEnforcementData {
  startEnforcement: StartEnforcementValue;
  fromDate: Iso8601DateString;
}

const mapStateToProps = (state: RootState) => ({
  showFromDate:
    (getFormValues('auth-enforcement')(state) as FormAuthEnforcementData)?.startEnforcement ===
    StartEnforcementValue.FromDate,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default reduxForm<FormAuthEnforcementData, unknown>({
  form: 'auth-enforcement',
})(connector(FormAuthEnforcement));
